/* ==========================================================================
   wishlist.js — favorites ("likes") for products.

   - One source of truth: the list of liked product ids in localStorage
     ("apykidz_wishlist"). Every heart on the page (cards and product page)
     reads from it, so they always agree, also after navigating or reloading.
   - The heart never adds to the cart.
   - Like counter: a stable starting number per product, 107 to 204, derived
     from the product id with a hash (same value on every visit, device and
     page, no storage needed), plus 1 when this visitor has liked it. It is
     computed in ONE place (likesFor) and shown everywhere: the product page
     pill [data-wish-count], the "N likes" line on product cards and search
     results [data-wish-likes]. The "+1" is per visitor: there is no backend
     adding up likes from everyone.
   ========================================================================== */
(function () {
  "use strict";

  var KEY = "apykidz_wishlist";
  var MIN_LIKES = 107;
  var LIKE_SPAN = 98; // 107..204 inclusive
  var LOCK_MS = 280;  // ignores extra clicks during the pop, so a double-tap can't like and unlike

  // Manual base-likes overrides for specific products (skips the hash below).
  // "10553240879393" = Cotton Hip Carrier, the homepage bestseller spotlight.
  var BASE_LIKES_OVERRIDE = { "10553240879393": 912 };

  var reduceMotion = window.matchMedia && window.matchMedia("(prefers-reduced-motion: reduce)").matches;
  var memory = null; // fallback when localStorage is blocked (private mode)

  function readList() {
    try {
      var v = JSON.parse(localStorage.getItem(KEY));
      if (Array.isArray(v)) return v.map(String);
    } catch (e) {}
    return memory ? memory.slice() : [];
  }
  function writeList(list) {
    memory = list.slice();
    try { localStorage.setItem(KEY, JSON.stringify(list)); } catch (e) {}
  }

  // FNV-1a hash of the product id -> 107..204, unless overridden above.
  function baseLikes(id) {
    var key = String(id);
    if (Object.prototype.hasOwnProperty.call(BASE_LIKES_OVERRIDE, key)) return BASE_LIKES_OVERRIDE[key];
    var s = key, h = 2166136261;
    for (var i = 0; i < s.length; i++) {
      h ^= s.charCodeAt(i);
      h = Math.imul(h, 16777619) >>> 0;
    }
    return MIN_LIKES + (h % LIKE_SPAN);
  }

  function likesFor(id, liked) {
    return baseLikes(id) + (liked ? 1 : 0);
  }

  function refresh() {
    var list = readList();

    // "N likes" lines on cards and search results (display only)
    document.querySelectorAll("[data-wish-likes]").forEach(function (el) {
      var id = el.getAttribute("data-wish-likes");
      var liked = list.indexOf(id) !== -1;
      el.classList.toggle("is-liked", liked);
      var c = el.querySelector("[data-wish-likes-count]");
      var n = String(likesFor(id, liked));
      if (c && c.textContent !== n) c.textContent = n;
    });

    document.querySelectorAll("[data-wish-toggle]").forEach(function (btn) {
      var id = btn.getAttribute("data-wish-toggle");
      var liked = list.indexOf(id) !== -1;
      var title = btn.getAttribute("data-product-title") || "";
      btn.classList.toggle("is-liked", liked);
      btn.setAttribute("aria-pressed", liked ? "true" : "false");
      btn.setAttribute("aria-label", (liked ? "Remove " : "Add ") + title + (liked ? " from wishlist" : " to wishlist"));

      var countEl = btn.querySelector("[data-wish-count]");
      if (countEl) {
        var n = likesFor(id, liked);
        if (countEl.textContent !== String(n)) {
          var hadValue = countEl.textContent !== "";
          countEl.textContent = n;
          if (hadValue && !reduceMotion) {
            countEl.classList.remove("is-bump");
            void countEl.offsetWidth;
            countEl.classList.add("is-bump");
          }
        }
      }
    });
  }

  function pop(btn, added) {
    if (reduceMotion) return;
    btn.classList.remove("is-popping");
    void btn.offsetWidth;
    btn.classList.add("is-popping");
    setTimeout(function () { btn.classList.remove("is-popping"); }, 480);

    if (!added) return;
    var spots = [[-22, -20], [0, -28], [22, -20], [-26, 4], [26, 4], [0, 24]];
    spots.forEach(function (d, i) {
      var b = document.createElement("span");
      b.className = "wish-burst";
      b.setAttribute("aria-hidden", "true");
      b.style.setProperty("--dx", d[0] + "px");
      b.style.setProperty("--dy", d[1] + "px");
      b.style.animationDelay = (i * 20) + "ms";
      btn.appendChild(b);
      setTimeout(function () { if (b.parentNode) b.parentNode.removeChild(b); }, 720);
    });
  }

  document.addEventListener("click", function (e) {
    var btn = e.target.closest("[data-wish-toggle]");
    if (!btn) return;
    e.preventDefault();
    e.stopPropagation();
    if (btn._wishLock) return;
    btn._wishLock = true;
    setTimeout(function () { btn._wishLock = false; }, LOCK_MS);

    var id = btn.getAttribute("data-wish-toggle");
    var list = readList();
    var idx = list.indexOf(id);
    var added = idx === -1;
    if (added) list.push(id); else list.splice(idx, 1);
    writeList(list);
    refresh();
    pop(btn, added);
    if (added && window.showToast) window.showToast("❤️ Added to your favorites");
    document.dispatchEvent(new CustomEvent("wishlist:change", { detail: { id: id, liked: added } }));
  });

  // Other tabs, and pages restored from the back/forward cache
  window.addEventListener("storage", function (e) { if (e.key === KEY) refresh(); });
  window.addEventListener("pageshow", refresh);

  window.ApykidzWishlist = {
    refresh: refresh,
    has: function (id) { return readList().indexOf(String(id)) !== -1; },
    likes: function (id) { return likesFor(id, this.has(id)); }
  };
  refresh();
})();
